import { Bar, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Card, EmptyState } from './Card.js';
import { useChartAnimation } from '../printScope.js';
import { fmtMonth, fmtPct, mergeTimeSeries, monthlyReturns } from '../utils.js';
import { rebaseFrom, type ReturnsFrom } from '../returnsFrom.js';
import { SERIES, bySign, GRID, AXIS, AXIS_SECONDARY, TOOLTIP_PROPS, PCT_AXIS_WIDTH_TITLED, pctAxisDomain } from '../chartTheme.js';
import type { PerformanceRow } from '@agio/core';

interface BenchRow {
  date: string;
  monthly_benchmark: number | null;
  cumulative_benchmark: number | null;
}

/** Shared merge logic behind BenchmarkChart — also drives benchmarkHasData so the
 * two can't drift apart. `available: false` mirrors the component's graceful
 * "no benchmark source at all" case (render nothing, not even an empty card). */
function benchmarkSeries(perf: PerformanceRow[], bench: BenchRow[], from: ReturnsFrom = null) {
  // Excel-extracted F/G series (carry manual fixes) win; native series as fallback.
  const extracted = perf.filter((p) => p.cumulative_return !== null);
  const hasExtractedBench = perf.some((p) => p.benchmark_cumulative !== null);
  if (bench.length === 0 && !hasExtractedBench) return { available: false, data: [] as ReturnType<typeof mergeTimeSeries> };

  let cumPortfolio: { date: string; portfolio: number | null }[];
  if (extracted.length >= 2) {
    cumPortfolio = extracted.map((p) => ({ date: p.date, portfolio: p.cumulative_return as number }));
  } else {
    const rets = monthlyReturns(perf);
    let cum = 0;
    cumPortfolio = rets.map((r) => {
      if (r.ret !== null) cum = (1 + cum) * (1 + r.ret) - 1;
      return { date: r.date, portfolio: cum };
    });
  }
  const benchSeries = hasExtractedBench
    ? perf.filter((p) => p.benchmark_cumulative !== null).map((p) => ({ date: p.date, benchmark: p.benchmark_cumulative }))
    : bench.map((b) => ({ date: b.date, benchmark: b.cumulative_benchmark }));
  // "Show returns from …": both lines start at the chosen month, counted from the month before it.
  return {
    available: true,
    data: mergeTimeSeries(rebaseFrom(cumPortfolio, from, ['portfolio']), rebaseFrom(benchSeries, from, ['benchmark'])),
  };
}

export const benchmarkHasData = (perf: PerformanceRow[], bench: BenchRow[]) => benchmarkSeries(perf, bench).data.length >= 2;

/**
 * Cumulative portfolio-vs-benchmark chart (port of the OSDK BenchmarkChart) with
 * the same green/red monthly-change bars as the סיכום chart, on a secondary axis.
 * Gracefully hides when the benchmark series is unavailable.
 */
export function BenchmarkChart({ perf, bench, from = null }: { perf: PerformanceRow[]; bench: BenchRow[]; from?: ReturnsFrom }) {
  const animate = useChartAnimation();
  const { available, data } = benchmarkSeries(perf, bench, from);
  if (!available) return null; // graceful degradation — same as OSDK
  if (data.length < 2) return <Card title="תשואה מול מדד ייחוס"><EmptyState label="אין מספיק נתונים" /></Card>;

  // Monthly delta bars: prefer the Excel E-column (manual fixes) else compute it.
  const moByMonth = new Map<string, number | null>();
  if (perf.some((p) => p.monthly_return !== null)) {
    for (const p of perf) if (p.monthly_return !== null) moByMonth.set(p.date.slice(0, 7), p.monthly_return);
  } else {
    for (const r of monthlyReturns(perf)) moByMonth.set(r.date.slice(0, 7), r.ret);
  }
  const rows = data.map((d) => ({ ...d, monthly_return: moByMonth.get(d.month) ?? null }));

  // Keep the delta bars in a top band (like the סיכום chart) rather than centered:
  // pad the monthly axis downward so its zero line sits near the top and the
  // cumulative lines own the lower two-thirds.
  const moVals = rows.map((r) => r.monthly_return).filter((v): v is number => v != null);
  const moMax = Math.max(0, ...moVals);
  const moMin = Math.min(0, ...moVals);
  const moDomain: [number, number] = [moMin - (moMax - moMin || 0.02) * 1.6, moMax];

  return (
    <Card title="תשואה מצטברת מול מדד ייחוס">
      <ResponsiveContainer width="100%" height={330}>
        <ComposedChart data={rows} margin={{ top: 4, right: 8, left: 8, bottom: 20 }}>
          <CartesianGrid {...GRID} />
          <XAxis
            dataKey="month"
            tickFormatter={(m: string) => fmtMonth(`${m}-01`)}
            {...AXIS}
            minTickGap={40}
            label={{ value: 'חודש', position: 'insideBottom', offset: -12, fill: 'var(--foreground)', fontSize: 12 }}
          />
          <YAxis
            yAxisId="cum"
            orientation="right"
            tickFormatter={(v: number) => fmtPct(v, 0)}
            {...AXIS}
            domain={pctAxisDomain(rows.flatMap((r) => [(r as { portfolio?: number | null }).portfolio, (r as { benchmark?: number | null }).benchmark]))}
            width={PCT_AXIS_WIDTH_TITLED}
            label={{ value: 'תשואה מצטברת (%)', angle: 90, position: 'insideRight', fill: 'var(--foreground)', fontSize: 12 }}
          />
          <YAxis
            yAxisId="mo"
            orientation="left"
            domain={moDomain}
            tickFormatter={(v: number) => fmtPct(v, 0)}
            {...AXIS_SECONDARY}
            width={PCT_AXIS_WIDTH_TITLED}
            label={{ value: 'שינוי חודשי (%)', angle: -90, position: 'insideLeft', fill: 'var(--foreground)', fontSize: 12 }}
          />
          <Tooltip
            {...TOOLTIP_PROPS}
            formatter={(v: number, name: string) => {
              const labels: Record<string, string> = {
                portfolio: 'תיק',
                benchmark: 'מדד ייחוס',
                monthly_return: 'שינוי חודשי',
              };
              return [fmtPct(v), labels[name] ?? name];
            }}
            labelFormatter={(m: string) => fmtMonth(`${m}-01`)}
          />
          <Bar yAxisId="mo" dataKey="monthly_return" radius={[2, 2, 0, 0]} name="monthly_return" isAnimationActive={animate}>
            {rows.map((d) => (
              <Cell key={d.month} fill={bySign(d.monthly_return ?? 0)} fillOpacity={0.75} />
            ))}
          </Bar>
          <Line yAxisId="cum" type="monotone" dataKey="portfolio" stroke={SERIES.portfolio} dot={false} strokeWidth={2.5} name="portfolio" isAnimationActive={animate} />
          <Line yAxisId="cum" type="monotone" dataKey="benchmark" stroke={SERIES.benchmark} dot={false} strokeWidth={2} name="benchmark" isAnimationActive={animate} />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="mt-2 flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
        <span><span className="me-1 inline-block h-1 w-4 rounded align-middle" style={{ background: SERIES.portfolio }} />תיק (מצטבר)</span>
        <span><span className="me-1 inline-block h-1 w-4 rounded align-middle" style={{ background: SERIES.benchmark }} />מדד ייחוס (מצטבר)</span>
        <span><span className="me-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: SERIES.positive }} />חודש חיובי</span>
        <span><span className="me-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: SERIES.negative }} />חודש שלילי</span>
      </div>
    </Card>
  );
}
